import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Zap, TrendingUp, Globe, Languages, Crown,
  CheckCircle2, ShieldCheck, Loader2, ArrowRight, MessageSquare, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { fetchMySubscription, startProSubscription, fetchBiometricStatus, uploadBiometricSelfie } from "@/lib/api";
import { verifyBiometric, isBiometricAvailable, isMobile } from "@/lib/webauthn";
import { SelfieCapture } from "@/components/SelfieCapture";
import { PaymentMethodPicker } from "@/components/PaymentMethodPicker";
import { WhatsappPayFallback } from "@/components/WhatsappPayFallback";
import { FideliteBadge } from "@/components/FideliteBadge";

const PRO_FEATURES = [
  { icon: Sparkles,       title: "AI Booster Titre & Description", desc: "L'IA réécrit vos annonces en mode 'vente premium' en 1 clic." },
  { icon: TrendingUp,     title: "AI Price Coach",                  desc: "Sait si vous êtes sous-évalué ou trop cher selon le marché UEMOA." },
  { icon: Globe,          title: "Publication multipays",           desc: "Une annonce, publiée à Abidjan, Dakar, Bamako, Ouaga, Lomé… en 1 clic." },
  { icon: Languages,      title: "Auto-traduction langues locales", desc: "Wolof et Bambara en 1 clic — vos acheteurs de Dakar et Bamako lisent dans leur langue." },
  { icon: Zap,            title: "Auto-bump 48 h",                  desc: "Vos annonces remontent automatiquement en tête des résultats toutes les 48h." },
  { icon: Crown,          title: "Badge « Vendeur Pro » doré",      desc: "Visible sur toutes vos annonces — confiance instantanée." },
  { icon: MessageSquare,  title: "AI WhatsApp Templates",           desc: "5 réponses prêtes à copier-coller, générées par l'IA." },
  { icon: ShieldCheck,    title: "Stats Pro temps réel",            desc: "Vues, favoris, conversations par annonce dans votre espace." },
];

export default function AbonnementsProPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const renewIntent = params.get("renew") === "1";
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [phone, setPhone] = useState("");
  const [payOpts, setPayOpts] = useState({ payment_method: "mobile_money", customer_country: "FR" });
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [proAmount, setProAmount] = useState(2000);
  const proAmountLabel = `${proAmount.toLocaleString("fr-FR")} FCFA`;

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchMySubscription()
      .then(setSub)
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) { toast.error("Connectez-vous d'abord pour vous abonner"); return; }
    setSubmitting(true);
    // Sur mobile : si l'empreinte est enregistree, on l'exige avant le paiement (en plus de l'OTP)
    if (isMobile() && (await isBiometricAvailable())) {
      try {
        const bio = await fetchBiometricStatus();
        if (bio.enrolled) {
          toast.info("Confirmez votre identite avec votre empreinte digitale…");
          await verifyBiometric();
          toast.success("Identite confirmee ✓");
          setSelfieOpen(true); // selfie de controle anti-fraude, puis paiement
          return;
        }
      } catch (bioErr) {
        toast.error(bioErr.message || "Verification biometrique echouee — paiement annule.");
        setSubmitting(false);
        return;
      }
    }
    await doPayment();
  };

  const onControlSelfie = async (photo) => {
    setSelfieOpen(false);
    if (photo) {
      try { await uploadBiometricSelfie({ image: photo, kind: "control", context: "payment" }); } catch { /* non-blocking */ }
    }
    await doPayment();
  };

  const doPayment = async () => {
    setSubmitting(true);
    try {
      const payer = payOpts.use_third_party && payOpts.payer_phone ? payOpts.payer_phone : (phone || user.phone_e164 || user.phone || "");
      const data = await startProSubscription(payer, payOpts.payment_method === "card" ? payOpts : {});
      if (data.dryrun) {
        toast.success("Mode test : abonnement activé immédiatement (CinetPay sandbox non configuré).");
      } else {
        toast.success("Redirection vers CinetPay…");
      }
      // External URL = CinetPay hosted checkout; internal = dryrun return route.
      if (data.payment_url.startsWith("http")) {
        window.location.href = data.payment_url;
      } else {
        navigate(data.payment_url);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "biometric_required") {
        toast.error("Verification par empreinte digitale requise avant le paiement. Reessayez.");
      } else {
        toast.error(detail || "Erreur lors du démarrage du paiement");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isActive = sub?.active === true;
  const daysLeft = sub?.current_period_end
    ? Math.max(0, Math.ceil((new Date(sub.current_period_end) - Date.now()) / 86400000))
    : null;
  const expiringSoon = isActive && daysLeft !== null && daysLeft <= 3;

  useEffect(() => {
    if (renewIntent && !loading) {
      document.getElementById("pro-renew-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [renewIntent, loading]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-orange-500" size={32} />
      </main>
    );
  }

  return (
    <main className="min-h-screen pb-20" data-testid="abonnements-pro-page">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider mb-4">
          <Crown size={14} /> Nouveau · Fodindin Pro
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 bg-clip-text text-transparent" style={{ backgroundImage: "var(--brand-text-gradient)" }}>
          Vendez 3x plus vite avec l'IA.
        </h1>
        <p className="text-base sm:text-lg text-[var(--ink-soft)] max-w-2xl mx-auto">
          L'IA Fodindin Pro réécrit vos titres, optimise vos prix, traduit vos annonces en
          wolof/bambara/dioula et les publie à travers les <b>7 pays UEMOA</b> en 1 clic.
        </p>
      </section>

      {/* Already Pro? */}
      {isActive && (
        <section className="max-w-3xl mx-auto px-6 mb-8" data-testid="pro-active-banner">
          <div className={`glass-card p-5 border-2 ${expiringSoon ? "border-amber-400 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} id="pro-renew-card">
            <div className="flex items-start gap-3">
              <CheckCircle2 className={`${expiringSoon ? "text-amber-600" : "text-emerald-600"} shrink-0 mt-0.5`} size={22} />
              <div className="flex-1">
                <div className={`font-bold ${expiringSoon ? "text-amber-800" : "text-emerald-700"}`}>
                  {expiringSoon ? `Votre abonnement Pro expire dans ${daysLeft} jour${daysLeft > 1 ? "s" : ""}` : "Vous êtes Fodindin Pro 🎉"}
                </div>
                <p className={`text-sm ${expiringSoon ? "text-amber-900/80" : "text-emerald-800/80"}`} data-testid="pro-period-end">
                  Abonnement actif jusqu'au {sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString("fr-FR") : "—"}.
                  {expiringSoon ? " Renouvelez maintenant pour ne rien perdre : +30 jours ajoutés à votre date actuelle." : " Profitez de toutes les fonctionnalités IA dès maintenant."}
                </p>
                {user && <div className="mt-3"><FideliteBadge onAmount={setProAmount} /></div>}
                <button
                  type="button"
                  onClick={handleSubscribe}
                  disabled={submitting}
                  className={`mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl font-bold text-white shadow-md transition disabled:opacity-50 ${expiringSoon ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg" : "bg-emerald-600 hover:bg-emerald-700"}`}
                  data-testid="pro-renew-btn"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  {submitting ? "Redirection…" : `Renouveler +30 jours — ${proAmountLabel}`}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Avant / Après */}
      <section className="max-w-5xl mx-auto px-6 mb-12" data-testid="avant-apres-section">
        <h2 className="text-2xl font-bold text-center mb-2">La différence Pro, en un coup d'œil</h2>
        <p className="text-center text-sm text-[var(--ink-soft)] mb-8">La même annonce, sans et avec Fodindin Pro.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {/* AVANT */}
          <div className="rounded-3xl border-2 border-dashed border-[var(--line)] p-5 bg-white/60" data-testid="avant-card">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-soft)] mb-3">Sans Pro</div>
            <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-white opacity-80">
              <div className="h-28 bg-gradient-to-br from-gray-200 to-gray-300 grid place-items-center text-gray-400 text-xs font-semibold">Photo de l'annonce</div>
              <div className="p-4">
                <div className="font-bold text-sm text-[var(--ink)]">Toyota Corolla 2018 à vendre</div>
                <div className="text-orange-600 font-bold text-sm mt-1">8 500 000 FCFA</div>
                <div className="text-[11px] text-[var(--ink-soft)] mt-1">Abidjan · Cocody</div>
              </div>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-[var(--ink-soft)]">
              <li>• Position n°47 dans les résultats</li>
              <li>• Visible en Côte d'Ivoire uniquement</li>
              <li>• Français uniquement</li>
              <li>• Aucun badge de confiance</li>
            </ul>
          </div>
          {/* APRES */}
          <div className="rounded-3xl border-2 border-amber-400 p-5 bg-gradient-to-br from-amber-50 to-white shadow-lg" data-testid="apres-card">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-3 inline-flex items-center gap-1"><Crown size={12} /> Avec Fodindin Pro</div>
            <div className="rounded-2xl border border-amber-200 overflow-hidden bg-white relative">
              <div className="h-28 bg-gradient-to-br from-amber-100 to-orange-200 grid place-items-center text-amber-500 text-xs font-semibold">Photo de l'annonce</div>
              <span className="absolute top-16 left-2 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 text-[10px] font-extrabold px-2 py-1 rounded-full shadow-md"><Crown size={10} /> PRO</span>
              <div className="p-4">
                <div className="font-bold text-sm text-[var(--ink)]">🚗 Toyota Corolla 2018 — OCCASION premier main, full options</div>
                <div className="text-orange-600 font-bold text-sm mt-1">8 500 000 FCFA</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Français</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Wolof</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Bambara</span>
                </div>
              </div>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm font-medium text-[var(--ink)]">
              <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> Position n°1 — remontée auto toutes les 48h</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> Visible dans les 7 pays UEMOA 🌍</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> Traduite en Wolof et Bambara</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> Badge Or 👑 + Stats en temps réel</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing card */}
      {!isActive && (
        <section className="max-w-md mx-auto px-6 mb-12">
          <div className="glass-card p-7 border-2 border-amber-300 shadow-xl relative overflow-hidden" data-testid="pro-pricing-card">
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-amber-200 rounded-full opacity-50" />
            <div className="relative">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-bold text-[var(--ink)]" data-testid="pro-price-amount">{proAmount.toLocaleString("fr-FR")}</span>
                <span className="text-xl font-semibold text-[var(--ink-soft)]">FCFA</span>
                <span className="text-sm text-[var(--ink-soft)]">/ mois</span>
                {proAmount < 2000 && <span className="text-sm line-through text-[var(--ink-soft)]">2 000</span>}
              </div>
              {user && <FideliteBadge onAmount={setProAmount} />}
              <p className="text-sm text-[var(--ink-soft)] mb-5">
                Paiement Mobile Money (Orange, Wave, MTN, Moov) ou carte bancaire internationale
              </p>
              <PaymentMethodPicker value={payOpts} onChange={setPayOpts} amount={proAmount} />
              <WhatsappPayFallback plan="Fodindin Pro" amount={proAmount} phone={phone || user?.phone_e164 || user?.phone} />
              <label className="block mb-4">
                <span className="text-xs font-semibold text-[var(--ink-soft)] uppercase tracking-wider">
                  {payOpts.payment_method === "card" ? "Téléphone de contact" : "Numéro Mobile Money"}
                </span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={user?.phone_e164 || user?.phone || "+225 07 12 34 56 78"}
                  data-testid="pro-phone-input"
                  className="mt-1 w-full !py-3 px-4 rounded-2xl border-2 border-[var(--line)] focus:border-amber-400 focus:ring-2 focus:ring-amber-200 outline-none transition"
                />
              </label>
              <button
                type="button"
                onClick={handleSubscribe}
                disabled={submitting || !user}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg hover:shadow-xl transition disabled:opacity-50"
                data-testid="pro-subscribe-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />}
                {submitting ? "Redirection…" : "Devenir Vendeur Pro"}
                {!submitting && <ArrowRight size={16} />}
              </button>
              {!user && (
                <p className="text-xs text-center mt-3 text-[var(--ink-soft)]">
                  Connectez-vous d'abord pour vous abonner
                </p>
              )}
            </div>
          </div>
          <p className="text-center text-xs text-[var(--ink-soft)] mt-4">
            🛡️ Paiement sécurisé via CinetPay · Sans engagement · Résiliable à tout moment
          </p>
        </section>
      )}

      {/* Features grid */}
      <section className="max-w-6xl mx-auto px-6 mb-16">
        <h2 className="text-2xl font-bold text-center mb-2">Ce que vous obtenez</h2>
        <p className="text-center text-sm text-[var(--ink-soft)] mb-10">
          Toutes ces fonctionnalités sont incluses dans votre abonnement Pro
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pro-features-grid">
          {PRO_FEATURES.map((f) => (
            <div
              key={f.title}
              className="glass-card p-5 hover:shadow-lg transition-all hover:-translate-y-1"
              data-testid={`pro-feature-${f.title.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
            >
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <f.icon size={20} className="text-amber-600" />
              </div>
              <h3 className="font-bold text-[var(--ink)] mb-1">{f.title}</h3>
              <p className="text-sm text-[var(--ink-soft)]">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust / FAQ */}
      <section className="max-w-3xl mx-auto px-6">
        <div className="glass-card p-6">
          <h3 className="font-bold text-lg mb-4">Questions fréquentes</h3>
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-semibold text-[var(--ink)]">Comment fonctionne le paiement ?</div>
              <p className="text-[var(--ink-soft)]">Vous payez 2 000 FCFA par mois via Orange Money, Wave, MTN MoMo ou Moov. Sécurisé par CinetPay.</p>
            </div>
            <div>
              <div className="font-semibold text-[var(--ink)]">Puis-je résilier à tout moment ?</div>
              <p className="text-[var(--ink-soft)]">Oui, sans engagement. Votre abonnement Pro reste actif jusqu'à la fin du mois payé.</p>
            </div>
            <div>
              <div className="font-semibold text-[var(--ink)]">Combien de pays sont inclus ?</div>
              <p className="text-[var(--ink-soft)]">Les 7 pays UEMOA : Côte d'Ivoire, Sénégal, Mali, Burkina Faso, Niger, Bénin, Togo.</p>
            </div>
          </div>
        </div>
      </section>
      <SelfieCapture
        open={selfieOpen}
        onDone={onControlSelfie}
        title="Photo de controle"
        description="Un selfie horodate est conserve comme preuve de securite pour ce paiement."
      />
    </main>
  );
}
